import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Plus, Image, Type, Mail, Share2, Minus, Space } from 'lucide-react';

export type BlockType = 'image' | 'text' | 'contact' | 'social' | 'divider' | 'spacer';

interface BlockTypeOption {
  type: BlockType;
  label: string;
  icon: React.ElementType;
  description: string;
}

const blockTypes: BlockTypeOption[] = [
  { type: 'image', label: 'Image/Logo', icon: Image, description: 'Add a logo or photo' },
  { type: 'text', label: 'Text', icon: Type, description: 'Name, title, or custom text' },
  { type: 'contact', label: 'Contact Row', icon: Mail, description: 'Email, phone with icon' },
  { type: 'social', label: 'Social Icons', icon: Share2, description: 'Social media links' },
  { type: 'divider', label: 'Divider', icon: Minus, description: 'Horizontal or vertical line' },
  { type: 'spacer', label: 'Spacer', icon: Space, description: 'Empty space' },
];

interface AddContentPopoverProps {
  onSelect: (type: BlockType) => void;
  children?: React.ReactNode;
}

export const AddContentPopover: React.FC<AddContentPopoverProps> = ({
  onSelect,
  children,
}) => {
  const [open, setOpen] = React.useState(false);

  const handleSelect = (type: BlockType) => {
    onSelect(type);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children || (
          <Button variant="ghost" size="sm" className="h-8 w-8 rounded-full">
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="center">
        <p className="text-xs font-medium text-muted-foreground mb-2 px-1">Add content</p>
        <div className="space-y-1">
          {blockTypes.map(({ type, label, icon: Icon, description }) => (
            <button
              key={type}
              onClick={() => handleSelect(type)}
              className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors text-left"
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-md bg-muted flex items-center justify-center">
                <Icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium">{label}</p>
                <p className="text-xs text-muted-foreground truncate">{description}</p>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
